import React, { useState } from 'react';
import {
  Box, Typography, Button, Chip, Stack, Paper, IconButton,
  Tooltip, LinearProgress, Divider, Alert,
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, Phone as PhoneIcon, Email as EmailIcon,
  OpenInNew as OpenIcon, Alarm as AlarmIcon,
} from '@mui/icons-material';
import { STATUS_CONFIG, TIMEZONE_LABELS, PRIORITY_COLORS } from './SalesDashboard';
import QuickNoteBar from './QuickNoteBar';
import StatusChanger from './StatusChanger';

// ─── Helper: local time for a timezone ────────────────────────────────────────
const localTime = (tz) => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit', minute: '2-digit', hour12: true,
      weekday: 'short', timeZone: tz,
    }).format(new Date());
  } catch { return ''; }
};

const isGoodHour = (tz) => {
  try {
    const h = parseInt(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone: tz }).format(new Date()));
    return h >= 8 && h < 20;
  } catch { return true; }
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;
const currency = (n) => n != null ? `$${Number(n).toLocaleString()}` : null;

// ─── Main DialerView ──────────────────────────────────────────────────────────
export default function DialerView({ leads, index, onIndexChange, onLeadUpdated, onOpenDetail, onStatusChange }) {
  const lead = leads[index];
  if (!lead) return null;

  const good = isGoodHour(lead.timezone);
  const progress = Math.round(((index + 1) / leads.length) * 100);

  const phone = lead.mobilePhone || lead.phone || lead.businessPhone;

  return (
    <Box>
      {/* Progress bar */}
      <Stack direction="row" alignItems="center" gap={2} mb={1.5}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ flex: 1, height: 6, borderRadius: 3 }}
        />
        <Typography variant="caption" color="text.secondary" noWrap>
          {index + 1} / {leads.length}
        </Typography>
      </Stack>

      {/* Navigation + Card */}
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <IconButton onClick={() => onIndexChange(Math.max(0, index - 1))} disabled={index === 0} sx={{ mt: 4 }}>
          <ChevronLeft />
        </IconButton>

        <LeadCard
          lead={lead}
          good={good}
          phone={phone}
          onLeadUpdated={onLeadUpdated}
          onOpenDetail={onOpenDetail}
          onStatusChange={onStatusChange}
        />

        <IconButton onClick={() => onIndexChange(Math.min(leads.length - 1, index + 1))} disabled={index === leads.length - 1} sx={{ mt: 4 }}>
          <ChevronRight />
        </IconButton>
      </Stack>

      {/* Keyboard hint */}
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
        Use arrow buttons to navigate leads
      </Typography>
    </Box>
  );
}

// ─── LeadCard ─────────────────────────────────────────────────────────────────
function LeadCard({ lead, good, phone, onLeadUpdated, onOpenDetail, onStatusChange }) {
  const status = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new_lead;

  return (
    <Paper
      elevation={3}
      sx={{
        flex: 1,
        maxWidth: 760,
        mx: 'auto',
        borderRadius: 3,
        overflow: 'hidden',
        border: '2px solid',
        borderColor: status.color,
      }}
    >
      {/* ── Top bar ──────────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: status.color, px: 2.5, py: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" gap={1} alignItems="center">
            <Chip
              label={status.label}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700, fontSize: '0.75rem' }}
            />
            <Chip
              label={lead.priority?.toUpperCase()}
              size="small"
              sx={{ bgcolor: PRIORITY_COLORS[lead.priority] || '#94a3b8', color: '#fff', fontWeight: 700, fontSize: '0.68rem' }}
            />
          </Stack>
          <Tooltip title="Open full details">
            <IconButton size="small" onClick={() => onOpenDetail(lead)} sx={{ color: '#fff' }}>
              <OpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box sx={{ p: 2.5 }}>

        {/* ── Business name & timezone ────────────────────────────────────────── */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
          <Typography variant="h5" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.2 }}>
            {lead.businessName || 'No Business Name'}
          </Typography>
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary">
              {TIMEZONE_LABELS[lead.timezone] || lead.timezone}
            </Typography>
            <Typography variant="body2" fontWeight={600} color={good ? 'success.main' : 'warning.main'}>
              {localTime(lead.timezone)} {good ? '✅' : '⚠️'}
            </Typography>
          </Box>
        </Stack>

        <Typography variant="body1" fontWeight={600} color="text.secondary" mb={0.25}>
          {[lead.firstName, lead.lastName].filter(Boolean).join(' ')}{lead.title ? ` · ${lead.title}` : ''}
        </Typography>
        {lead.industry && <Typography variant="caption" color="text.disabled">{lead.industry}</Typography>}

        <Divider sx={{ my: 1.5 }} />

        {/* ── Info grid ───────────────────────────────────────────────────────── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', mb: 2 }}>
          <InfoRow label="Phone" value={phone} href={phone ? `tel:${phone}` : null} />
          <InfoRow label="Email" value={lead.email} href={lead.email ? `mailto:${lead.email}` : null} />
          <InfoRow label="Monthly Revenue" value={currency(lead.monthlyRevenue)} />
          <InfoRow label="Requested Funding" value={currency(lead.requestedAmount)} />
          <InfoRow label="Business Start" value={fmtDate(lead.businessStartDate)} />
          <InfoRow label="Entity Type" value={lead.businessEntityType} />
          <InfoRow label="Address" value={[lead.businessCity, lead.businessState].filter(Boolean).join(', ')} />
          <InfoRow label="Source" value={lead.source?.replace(/_/g, ' ')} />
          {lead.lastContactedAt && <InfoRow label="Last Contact" value={fmtDateTime(lead.lastContactedAt)} />}
          {lead.nextFollowUpDate && <InfoRow label="Follow-up" value={fmtDate(lead.nextFollowUpDate)} />}
          {lead.totalCallAttempts > 0 && <InfoRow label="Call Attempts" value={lead.totalCallAttempts} />}
        </Box>

        {/* ── Call-to-action buttons ───────────────────────────────────────────── */}
        <Stack direction="row" gap={1.5} flexWrap="wrap">
          {phone && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PhoneIcon />}
              href={`tel:${phone}`}
              component="a"
              size="large"
              sx={{ fontWeight: 700, flex: 1, minWidth: 140 }}
            >
              Call {phone}
            </Button>
          )}
          {lead.email && (
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => onOpenDetail(lead)}
              size="large"
              sx={{ flex: 1, minWidth: 120 }}
            >
              Send Email
            </Button>
          )}
        </Stack>

        {/* ── Last note preview ───────────────────────────────────────────────── */}
        {lead.notes && lead.notes.length > 0 && (
          <Box mt={2} sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5}>
              Last Note ({fmtDateTime(lead.notes[0].createdAt)})
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {lead.notes[0].content}
            </Typography>
          </Box>
        )}

        {!good && (
          <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
            Outside typical calling hours for {TIMEZONE_LABELS[lead.timezone]?.split(' ')[0]} time zone
          </Alert>
        )}

        {/* ── Status changer ───────────────────────────────────────────────────── */}
        <Box mt={2}>
          <StatusChanger currentStatus={lead.status} onStatusChange={(s) => onStatusChange(lead._id, s)} />
        </Box>

        {/* ── Quick note bar ───────────────────────────────────────────────────── */}
        <Box mt={1.5}>
          <QuickNoteBar leadId={lead._id} onLeadUpdated={onLeadUpdated} />
        </Box>
      </Box>
    </Paper>
  );
}

// ─── InfoRow ──────────────────────────────────────────────────────────────────
function InfoRow({ label, value, href }) {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.disabled" display="block" lineHeight={1.2}>{label}</Typography>
      {href
        ? <Typography variant="body2" component="a" href={href} sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 500 }}>{value}</Typography>
        : <Typography variant="body2" fontWeight={500}>{value}</Typography>
      }
    </Box>
  );
}
